import { MAX_ACTIVE_MEMBERS } from './access';
import { normalizeEmail } from './types';
import type { Identity } from './types';

// --- the address, before there is an invitation ---------------------------------------------

/**
 * Long enough for any real address (RFC 5321), short enough to bound a column.
 *
 * This and the shape below used to live in `worker/handlers/invitations.ts` as the private
 * `readEmail`. They are here now because two callers need the same answer: the Worker, which
 * is the one that decides, and the browser, which refuses early so a teacher does not type
 * five addresses and learn on submit that the second one had a space in it. The browser's copy
 * is a courtesy — it is the same function, so the two cannot disagree.
 */
export const MAX_EMAIL_LENGTH = 254;
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** An address in the one form the roster, the match and the unique index all read, or null. */
export function normalizeInviteEmail(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const email = normalizeEmail(raw);
  if (email.length === 0 || email.length > MAX_EMAIL_LENGTH || !EMAIL_SHAPE.test(email)) return null;
  return email;
}

/**
 * How many addresses may be gathered before a group is created.
 *
 * Derived from `MAX_ACTIVE_MEMBERS` rather than written out, and one less than it, because the
 * teacher creating the group is its first member. Twenty-five seats, one of them already
 * taken, twenty-four invitations — spelling `24` here would be a second copy of the cap that
 * drifts the day the first one moves.
 */
export const MAX_INVITE_ADDRESSES = MAX_ACTIVE_MEMBERS - 1;

export type AddressRefusal = 'invalid' | 'self' | 'duplicate' | 'list_full';

/**
 * A pasted list, split into what the teacher typed (R1).
 *
 * Newlines, commas, semicolons and any whitespace all separate, because that covers a column
 * pasted from a spreadsheet, an address line copied from a mail client and a list typed by
 * hand. Entries come back unnormalized so a bad one can be shown back exactly as it was
 * typed; `decideInviteAddress` is what judges each one.
 */
export function splitInviteList(raw: string): string[] {
  return raw.split(/[\s,;]+/).filter((entry) => entry.length > 0);
}

export type AddressDecision =
  | { added: true; email: string }
  | { added: false; reason: AddressRefusal };

export interface AddAddressInput {
  /** What the teacher typed, unnormalized. */
  raw: string;
  /** The addresses already on the list, already normalized. */
  existing: readonly string[];
  /** The teacher's own address, or null when the session has not told us one. */
  self: string | null;
}

/**
 * Whether one typed address may join the list being gathered. Pure — no network, no clock.
 *
 * The order is the order the reasons matter in. An address that is not an address cannot be
 * compared to anything, so shape comes first; the teacher's own address is refused before the
 * duplicate check so that typing it twice still says the useful thing rather than "already on
 * the list". `self` being null refuses nothing: an unknown address is not a match, and the
 * Worker is the one that decides anyway.
 */
export function decideInviteAddress({ raw, existing, self }: AddAddressInput): AddressDecision {
  const email = normalizeInviteEmail(raw);
  if (email === null) return { added: false, reason: 'invalid' };
  if (self !== null && email === normalizeEmail(self)) return { added: false, reason: 'self' };
  if (existing.includes(email)) return { added: false, reason: 'duplicate' };
  if (existing.length >= MAX_INVITE_ADDRESSES) return { added: false, reason: 'list_full' };
  return { added: true, email };
}

/**
 * Whether one invitation may be accepted by one caller. Pure — no clock, no database.
 *
 * This is the sharpest function in the package: the invitation link is the whole
 * credential, it survives forwarding and browser history for seven days, and a wrong
 * branch here hands a group to whoever holds the link. It was written test-first for that
 * reason.
 *
 * The decision is advisory to the client and authoritative only to the server. Every
 * refusal below answers the browser with one indistinguishable `invitation_unusable`
 * (R10); the specific reason stays server-side, stamped on `invitations.last_refusal` and
 * emitted as an event, so an admin can see *why* their colleague bounced without the
 * client being able to probe which tokens exist.
 */

/** The columns of `invitations` this decision reads. Nothing else is needed or allowed. */
export interface InvitationRecord {
  /** Already `lower(trim(...))` in the database — see `normalizeEmail`. */
  emailNormalized: string;
  /** ISO-8601, fixed width, so this comparison and the SQL accept guard agree. */
  expiresAt: string;
  consumedAt: string | null;
  revokedAt: string | null;
  /**
   * Set when the invitee said no (R11). A decline stamps `revokedAt` in the same write so the
   * SQL guard and the live-slot index need no new clause; this field exists so the refusal
   * and the admin's list can say *declined* rather than *revoked*. Optional so a record read
   * before migration 0005 still decides.
   */
  declinedAt?: string | null;
}

export type InvitationRefusal =
  | 'declined'
  | 'expired'
  | 'revoked'
  | 'consumed'
  | 'wrong_recipient'
  | 'unverified_address';

export type InvitationDecision =
  | { accept: true }
  | { accept: false; reason: InvitationRefusal };

export interface DecideInvitationInput {
  invitation: InvitationRecord;
  /** ISO-8601 from `Stamp.now()`. The core never asks what time it is. */
  now: string;
  identity: Pick<Identity, 'email' | 'emailVerified'>;
}

const ACCEPT: InvitationDecision = { accept: true };
const refuse = (reason: InvitationRefusal): InvitationDecision => ({ accept: false, reason });

/**
 * Precedence is fixed and tested, because `last_refusal` is read by a human: revoked,
 * then consumed, then expired, then the caller. The invitation's own state outranks the
 * caller's, since a revoked token is nobody's to accept no matter who asks.
 */
export function decideInvitation({ invitation, now, identity }: DecideInvitationInput): InvitationDecision {
  // Ahead of `revoked`, which a decline also stamps: the admin should read the true reason.
  if (invitation.declinedAt != null) return refuse('declined');
  if (invitation.revokedAt !== null) return refuse('revoked');
  if (invitation.consumedAt !== null) return refuse('consumed');

  // An unparseable or missing timestamp is expired, not accepted. The one direction a
  // parse failure is allowed to fall is closed (R21).
  const expiresAt = Date.parse(invitation.expiresAt);
  const at = Date.parse(now);
  if (!Number.isFinite(expiresAt) || !Number.isFinite(at)) return refuse('expired');
  // `>=` so the boundary instant is already expired, matching the SQL guard's
  // `expires_at > ?`.
  if (at >= expiresAt) return refuse('expired');

  // KTD14. `emailVerified` must be exactly `true`. False, absent and undefined are all
  // unverified — the plan's permissive "an unresolved flag is not an off flag" idiom
  // belongs to kill switches and must never reach this line, because here it would admit
  // whoever holds the link. A null or blank address is unverified for the same reason:
  // there is nothing to have verified.
  const claimed = normalizeEmail(identity.email);
  if (identity.emailVerified !== true || claimed.length === 0) return refuse('unverified_address');

  // Whole-address equality, never a prefix or suffix test. `invitee@school.org.evil.test`
  // ends with the invited address and is a different person; that is what the property
  // test in `tests/core/invitations.property.test.ts` exists to hold.
  if (claimed !== normalizeEmail(invitation.emailNormalized)) return refuse('wrong_recipient');

  return ACCEPT;
}

/** Seven days (R7), as milliseconds, so the shell never spells the number itself. */
export const INVITATION_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** Three days: long enough that silence means something, short enough to act before expiry. */
export const WAITING_AFTER_MS = 3 * 24 * 60 * 60 * 1000;

/** The columns of a listed invitation the admin's wait label reads (R7, KTD12). */
export interface WaitingInvitation {
  createdAt: string;
  expiresAt: string;
  declinedAt: string | null;
  lastRefusal: string | null;
}

/**
 * What the admin should do next, as a key the browser turns into words.
 *
 * Precedence is the order the facts matter in: a decline is final, an expired link cannot be
 * fixed by checking anything, a refusal names the actual problem, and only then does elapsed
 * time say anything. An unreadable timestamp reads as expired — the label that asks for a new
 * link — rather than as recent, which would ask the admin to wait on a link that may be dead.
 */
export type WaitLabel = 'recent' | 'waiting' | 'expired' | 'declined' | `refused:${string}`;

export function waitLabel(row: WaitingInvitation, now: string): WaitLabel {
  if (row.declinedAt !== null) return 'declined';
  const at = Date.parse(now);
  const expires = Date.parse(row.expiresAt);
  const created = Date.parse(row.createdAt);
  if (!Number.isFinite(at) || !Number.isFinite(expires) || !Number.isFinite(created)) return 'expired';
  if (at >= expires) return 'expired';
  if (row.lastRefusal !== null) return `refused:${row.lastRefusal}`;
  return at - created >= WAITING_AFTER_MS ? 'waiting' : 'recent';
}

/**
 * When a link minted at `now` stops working.
 *
 * Pure, and fixed-width `toISOString()` on the way out, because the TEXT comparison in the
 * accept guard (`expires_at > ?`) is a string comparison: a stored `2026-9-29T...` would
 * sort below every well-formed stamp and expire on the spot. Returns null for an
 * unparseable `now` rather than inventing a date — the caller refuses instead.
 */
export function expiresAtFrom(now: string): string | null {
  const at = Date.parse(now);
  if (!Number.isFinite(at)) return null;
  return new Date(at + INVITATION_TTL_MS).toISOString();
}

/**
 * Every reason an accept can be refused, including the three the SQL guard owns.
 *
 * `decideInvitation` cannot see the last three: whether the inviter is still an admin and
 * whether the group is full are facts about other rows, decided inside the guarded batch so
 * that two concurrent accepts cannot both read "there is one seat left". They are named here
 * because they end up in the same three places every other refusal does — the event, the log,
 * and `invitations.last_refusal` — and the client is told the same `invitation_unusable` by
 * all of them (R10).
 */
export type AcceptRefusal =
  | InvitationRefusal
  | 'unknown_token'
  | 'inviter_not_admin'
  | 'cap_reached';

export interface ExplainRefusalInput {
  /** Null when no row carries that token hash. */
  invitation: InvitationRecord | null;
  now: string;
  identity: Pick<Identity, 'email' | 'emailVerified'>;
  /** Whether the teacher who created the link still holds an active admin membership. */
  inviterIsActiveAdmin: boolean;
  /** Active members of the group, re-read after the guarded batch refused. */
  activeMembers: number;
}

/**
 * Which refusal to record, once the batch has already refused.
 *
 * **This chooses the message and never the decision.** The guarded statements decided; this
 * runs over a re-read afterwards, purely so the admin's pending list can say something
 * useful and the event can say which branch fired. Calling it earlier — and acting on it —
 * would be the read-then-write race the guard exists to prevent (KTD9).
 *
 * The order matches `decideInvitation`'s: the invitation's own state outranks the caller's,
 * and the two facts only the guard can see come last. The final fallback is the honest one:
 * a re-read that shows a live invitation, an active inviter and room in the group means
 * another request consumed the token between the batch and the re-read.
 */
export function explainAcceptRefusal(input: ExplainRefusalInput): AcceptRefusal {
  if (input.invitation === null) return 'unknown_token';

  const decision = decideInvitation({
    invitation: input.invitation,
    now: input.now,
    identity: input.identity,
  });
  if (!decision.accept) return decision.reason;

  if (!input.inviterIsActiveAdmin) return 'inviter_not_admin';
  if (input.activeMembers >= MAX_ACTIVE_MEMBERS) return 'cap_reached';
  return 'consumed';
}
